import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { Spinner } from "@mixitone/components";
import { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import { globalSearchPlaceholder, isMacOS, modKeyShortcut } from "lib/keyboardShortcut";
import { GLOBAL_SEARCH_MIN_LENGTH, type GlobalSearchResult } from "lib/globalSearchTypes";
import tauri from "lib/tauri";
import { useEffect, useRef, useState } from "react";
import GlobalSearchController from "./GlobalSearchController";

function useMinWidthMediaQuery(minWidthPx: number): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia(`(min-width: ${minWidthPx}px)`).matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${minWidthPx}px)`);
    const onChange = () => setMatches(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, [minWidthPx]);

  return matches;
}

const GlobalSearch: React.FC = () => {
  const controller = GlobalSearchController.use();
  const { query, completedQuery, groupedResults, loading, results, forceVisible } = controller.state;
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wideViewport = useMinWidthMediaQuery(1024);
  const macOverlayTitlebar = tauri.enabled && isMacOS();
  const placeholder = globalSearchPlaceholder(wideViewport);

  useEffect(() => {
    if (!forceVisible) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [forceVisible]);

  const hasResults = results.length > 0;
  const isTypingAhead = inputValue !== query;
  const showOptions =
    query.length >= GLOBAL_SEARCH_MIN_LENGTH &&
    !isTypingAhead &&
    (loading || hasResults || completedQuery === query);

  return (
    <div
      data-testid="global-search-root"
      data-tauri-drag-region={false}
      className={clsx(
        "min-w-0 flex-1 items-center",
        macOverlayTitlebar
          ? "justify-center pl-0 pr-2 sm:pl-1 sm:pr-3 short:pr-1"
          : "justify-center px-3 short:px-2",
        forceVisible ? "flex" : tauri.enabled ? "flex" : "hidden sm:flex",
      )}
    >
      <Combobox
        value={null as GlobalSearchResult | null}
        onChange={(result) => {
          setInputValue("");
          void controller.actionSelect(result);
        }}
        nullable
      >
        <div
          className={clsx(
            "relative w-full",
            macOverlayTitlebar ? "max-w-xs lg:max-w-[22rem] xl:max-w-sm 2xl:max-w-md" : "max-w-md",
          )}
        >
          <ComboboxInput
            ref={inputRef}
            data-tauri-drag-region={false}
            data-testid="global-search-input"
            data-1p-ignore
            autoComplete="off"
            autoCorrect="off"
            placeholder={placeholder}
            title={`Global search (${modKeyShortcut()})`}
            className={clsx(
              "global-search-input w-full rounded-md border border-slate-600 bg-slate-700/80 py-1.5 pr-9 pl-3 text-sm text-slate-50 short:py-1 short:pl-2 short:pr-8",
              "placeholder:text-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500",
            )}
            value={inputValue}
            onChange={(event) => {
              const value = event.target.value;
              setInputValue(value);
              controller.actionQueueSearch(value);
            }}
            onBlur={() => controller.actionBlur()}
          />
          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-slate-400 short:right-1.5">
            {loading ? (
              <Spinner size={16} className="fill-slate-400" />
            ) : (
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            )}
          </div>
        </div>
        {showOptions && (
          <ComboboxOptions
            anchor="bottom start"
            className="z-50 mt-1 max-h-96 w-[var(--input-width)] overflow-y-auto rounded-md border border-slate-600 bg-slate-800 py-1 shadow-lg"
          >
            {!loading && !hasResults && (
              <div className="px-3 py-2 text-sm text-slate-400">No results found</div>
            )}
            {groupedResults.map((group) => (
              <div key={group.type}>
                <div className="px-3 py-1.5 text-xs font-semibold tracking-wide text-slate-400 uppercase">
                  {group.label}
                </div>
                {group.results.map((result) => (
                  <ComboboxOption
                    key={`${result.type}-${result.id}`}
                    value={result}
                    className="cursor-pointer px-3 py-2 ui-active:bg-slate-700 ui-not-active:bg-slate-800"
                  >
                    <div className="text-sm font-medium text-slate-50">{result.title}</div>
                    {(result.subtitle || result.clubName) && (
                      <div className="text-xs text-slate-400">
                        {[result.subtitle, result.clubName].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </ComboboxOption>
                ))}
              </div>
            ))}
          </ComboboxOptions>
        )}
      </Combobox>
    </div>
  );
};

const GlobalSearchScoped = GlobalSearchController.scope(ApplicationView(GlobalSearch));
export default GlobalSearchScoped;
